goog.provide('yext.m4.codeeditor.CodeEditor');

goog.require('yext.m4.errors.DiagnosticData');
goog.require('yext.m4.errors.ErrorsService');
goog.require('yext.m4.routes');

/**
 * Renders the code editor.
 *
 * @author Varun Vijayaraghavan (vvijayaraghavan@yext.com)
 */
yext.m4.codeeditor.CodeEditor = class {
  /**
   * @param {!HTMLElement}                  domElement
   * @param {!string}                       monacoWorkerRoot  - The URI path to the monaco-workers dir.
   *                                                            For example, public/js/monaco-workers/
   * @param {!yext.m4.errors.ErrorsService} errorsService     - The state service to add / remove errors.
   * @param {!string}                       editorSettingsUriPrefix   - The URI base path to fetch
                                              editor settings. Supported values are
                                              'configeditor' and 'adminconsole'

   * @param {boolean=}                      darkMode          - Makes it use the vs-dark theme.
   *                                                            Default is false.
   */
  constructor(domElement, monacoWorkerRoot, errorsService, editorSettingsUriPrefix, darkMode) {
    this._codeEditor = null;
    this._domElement = domElement;
    this._schemaMap = {};
    this._schemaAssociations = {};
    this._snippetsMap = {};
    this._autoCompletions = {};
    this._errorsMap = {}; /* resourcePath -> Diagnostic*/
    this._warningsMap = {}; /* resourcePath -> Diagnostic*/
    this._decorationsMap = {}; /* resourcePath -> {monacoDecorationId, Diagnostic} tracks the current state of decorations*/
    this._errorsService = errorsService;
    this._editorSettingsUriPrefix = editorSettingsUriPrefix;

    // this handler is used to detect when a line with a decoration is changed, which will then remove the error.
    this._modelContentChangeHandlerMap = {}; /* resourcePath -> modelContentChangeHandler*/
    this._initializeCodeEditor(monacoWorkerRoot, darkMode ? 'vs-dark' : 'vs-light');
  }

  /**
   * Gets the text currently displayed on the code editor.
   *
   * @return {!string} The code editor text
   */
  getValue() {
    return this._codeEditor.getValue();
  }

  /**
   * Inserts the JSON template associated with the resourceType.
   *
   * Internally, this has a few functions.
   * - Create the file, if it does not exist.
   * - If it exists, use that as the current model.
   * - Fetch JSON snippet from the server if necessary, and cache locally.
   * - Set schema associations for this file.
   * - If the fetch JSON Snippet fails, it still creates the model with an empty value.
   *
   * @param {!string} resourceType  - The resource type, for example km/custom-field
   * @param {!string} namespace - Whether or not the path provided includes the namespace
   * @param {!string} resourcePath  - The path associated with the resource. Excludes the namespace.
   *                                  For example: km/custom-field/test.json
   * @param {!Function} callback    - Callback with parameter error, if there was an error.
   */
  setTemplateValue(resourceType, namespace, resourcePath, callback) {
    this._fetchAndUpdateJSONConfigurations(resourceType, namespace, resourcePath, null, err => {
      if (err) {
        console.warn('Error setting JSON Configurations', err);
      }
      const snippetString = this._snippetsMap[resourceType] ? this._snippetsMap[resourceType] : '';
      this._setValue(snippetString, resourcePath);
      callback(err);
    });
  }

  /**
   * Sets the value of the text editor, and loads and updates the JSON Schema Configurations, if
   * relevant.
   *
   * If the backend call to fetch JSON Schema fails, this will fallback to setting the value without
   * the JSON Schema.
   *
   * Creates a new monaco model, if it does not exist.
   *
   * @param {!string}   text          - The contents of the file
   * @param {!string} namespace
   * @param {!string}   resourcePath  - The resource path, for ex: km/custom-field/example.json
   * @param {!Function} callback      - Callback on completion. It passes the error as the first
   *                                    parameter.
   *
   * @return {*}
   */
  setValue(text, namespace, resourcePath, callback) {
    if (this._inferLanguageMode(resourcePath) !== 'json') {
      this._setValue(text, resourcePath);
      return callback();
    }
    let typeAndVersion = this._inferResourceTypeAndVersionFromJsonString(text);
    if (!typeAndVersion['resourceType'] || !typeAndVersion['version']) {
      this._setValue(text, resourcePath);
      return callback();
    }
    this._fetchAndUpdateJSONConfigurations(
      typeAndVersion['resourceType'],
      namespace,
      resourcePath,
      typeAndVersion['version'],
      err => {
        if (err) {
          console.warn('Error setting JSON Configurations', err);
        }
        this._setValue(text, resourcePath);
        callback(err);
      });
  }

  /**
   * Makes the code editor read only.
   *
   * @param {boolean} readOnly if the editor needs to be read only
   */
  updateReadOnly(readOnly) {
    this._codeEditor.updateOptions({readOnly: readOnly});
  }

  /**
   * Makes the editor remeasure its container.
   */
  remeasure() {
    this._codeEditor.layout();
  }

  /**
   * Sets the text value of the code editor, without setting any JSON Schema Configurations.
   * Creates a new monaco model, if it does not exist.
   *
   * This should be typically used directly when there's no need to load JSON Schema based
   * information for this specific file.
   *
   * @param {!string} text          - The text to display in the code editor
   * @param {!string} resourcePath  - The resource path associated with the resource.
   *                                  For example, km/custom-field/test.json
   */
  setValueSimple(text, resourcePath) {
    this._setValue(text, resourcePath);
  }

  /**
   * Sets the text value of the code editor and loads autocomplete snippets for the given schema.
   * This will always create a new monaco model, even if one exists already.
   *
   * @param {!string} text The text to display in the code editor
   * @param {!string} resourcePath The resource path associated with the resource.
   *                               For example, km/field/test.json
   * @param {!string} resourceType The resource type. For example, km/field
   * @param {!Object} schema The schema for the resource
   * @param {?Array<string>} fields The fields to filter for in the schema
   */
  setValueWithNewModel(text, resourcePath, resourceType, schema, fields) {
    this._schemaMap[resourceType] = schema;
    this._setValueWithNewModel(text, resourcePath);

    if (this._schemaAssociations[resourcePath]
      && this._schemaMap[resourceType]
      && this._autoCompletions[resourceType]) {
      return;
    }

    this._schemaAssociations[resourcePath] = resourceType;
    const requestBody = {
      'schema': schema,
      'fields': fields,
    };

    $.ajax({
      method: 'POST',
      url: yext.m4.routes.generateAutocompleteSnippets({}),
      data: JSON.stringify(requestBody),
      contentType: 'application/json',
    }).done(response => {
      this._autoCompletions[resourceType] =
        this._addMonacoAutoCompletionString(response['autoCompletions']);
      this._updateJSONDiagnostics();
    });
  }

  /**
     * Sets the text value of the code editor, without setting any JSON Schema Configurations.
     * This will always create a new monaco model, even if one exists already.
     *
     * This should be typically used directly when there's no need to load JSON Schema based
     * information for this specific file.
     *
     * @param {!string} text The text to display in the code editor
     * @param {!string} resourcePath The resource path associated with the resource.
     *                               For example, km/custom-field/test.json
     */
  setValueSimpleWithNewModel(text, resourcePath) {
    this._setValueWithNewModel(text, resourcePath);
  }

  /**
   * This is the entry point to set errors and warnings. The CodeEditor renders the decorations
   * associated with the errors and warnings, after resetting the current state.
   *
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} errors
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} warnings
   */
  setCodeDiagnostics(errors, warnings) {
    // Reset the state of errors
    this._errorsMap = {};
    this._warningsMap = {};
    // Also reset all the current decorations on the editor, it's going to re-applied.
    this._resetAllDecorations();

    errors.filter(e => !!e.fileLocation).forEach(e => {
      if (!this._errorsMap[e.fileLocation.filePath]) {
        this._errorsMap[e.fileLocation.filePath] = [e];
      } else {
        this._errorsMap[e.fileLocation.filePath].push(e);
      }
    });

    warnings.filter(w => !!w.fileLocation).forEach(w => {
      if (!this._warningsMap[w.fileLocation.filePath]) {
        this._warningsMap[w.fileLocation.filePath] = [w];
      } else {
        this._warningsMap[w.fileLocation.filePath].push(w);
      }
    });

    this._updateAllModelDiagnostics();
  }

  /**
   * This is the entry point to remove errors and warnings. The CodeEditor removes the decorations
   * associated with the errors and warnings.
   *
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} errors    - Removed errors
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} warnings  - Removed warnings
   */
  removeCodeDiagnostics(errors, warnings) {
    const relevantErrors = errors.filter(e => !!e.fileLocation);
    const relevantWarnings = warnings.filter(w => !!w.fileLocation);

    if (!relevantErrors.length && !relevantWarnings.length) {
      return;
    }

    const removedByResource = {};

    relevantErrors.concat(relevantWarnings).forEach(e => {
      if (!removedByResource[e.fileLocation.filePath]) {
        removedByResource[e.fileLocation.filePath] = [e];
      } else {
        removedByResource[e.fileLocation.filePath].push(e);
      }
    });

    Object.keys(removedByResource).forEach(resourcePath =>
      this._removeDecorationsState(resourcePath, removedByResource[resourcePath]));
  }

  /**
   * Moves the cursor to the diagnostic
   * @param {!string} resourcePath
   * @param {!yext.m4.errors.DiagnosticData.Diagnostic} diagnostic
   */
  jumpToDiagnostic(resourcePath, diagnostic) {
    const model = monaco.editor.getModel(this._getModelUri(resourcePath));
    const decorationObj = (this._decorationsMap[resourcePath] || []).find(d => d['diagnostic'] === diagnostic);
    const decorationId = decorationObj['decorationId'];
    const decorationRange = model.getDecorationRange(decorationId);
    const row = decorationRange['startLineNumber'];
    this._codeEditor.revealLineInCenter(row);
    this._codeEditor.setPosition({'lineNumber': row, 'column': 1});
  }

  /**
   * @param {!string}                                           resourcePath
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} removedDiagnostics
   * @private
   */
  _removeDecorationsState(resourcePath, removedDiagnostics) {
    const model = monaco.editor.getModel(this._getModelUri(resourcePath));
    const decorationObjects = this._decorationsMap[resourcePath] || [];
    const errorsForModel = this._errorsMap[resourcePath] || [];
    const warningsForModel = this._warningsMap[resourcePath] || [];

    if (!model) {
      return;
    }

    if (!decorationObjects.length) {
      return;
    }

    if (!errorsForModel.length && !warningsForModel.length) {
      return;
    }

    const invalidDecorationIds = [];
    removedDiagnostics.forEach(diagnostic => {
      const decorationObject = decorationObjects
        .find(decorationObject => decorationObject['diagnostic'] === diagnostic);
      if (decorationObject) {
        invalidDecorationIds.push(decorationObject['decorationId']);
      }
    });

    if (!invalidDecorationIds.length) {
      return;
    }

    this._codeEditor.deltaDecorations(invalidDecorationIds, []);
    this._errorsMap[resourcePath] = errorsForModel.filter(e => !removedDiagnostics.includes(e));
    this._warningsMap[resourcePath] = warningsForModel.filter(w => !removedDiagnostics.includes(w));
    this._decorationsMap[resourcePath] = decorationObjects
      .filter(d => !invalidDecorationIds.includes(d['decorationId']));
  }


  /**
   * @private
   */
  _updateAllModelDiagnostics() {
    const currentModels = monaco.editor.getModels();
    for (const model of currentModels) {
      const resourcePath = this._getResourcePath(model['uri']);
      const errors = this._errorsMap[resourcePath] ? this._errorsMap[resourcePath] : [];
      const warnings = this._warningsMap[resourcePath] ? this._warningsMap[resourcePath] : [];

      if (errors.length || warnings.length) {
        // register content change handler for model
        if (!this._modelContentChangeHandlerMap[resourcePath]) {
          const disposableHandler = model.onDidChangeContent(event =>
            this._checkAndRemoveErrors(model, event));
          this._modelContentChangeHandlerMap[resourcePath] = disposableHandler;
        }
        this._applyNewErrorsAndWarnings(errors, warnings, model, resourcePath);
      } else if (this._modelContentChangeHandlerMap[resourcePath]) {
        // no errors or warnings. let's dispose the event handler.
        this._modelContentChangeHandlerMap[resourcePath].dispose();
        delete this._modelContentChangeHandlerMap[resourcePath];
      }
    }
  }

  /**
   * @private
   */
  _resetAllDecorations() {
    const currentModels = monaco.editor.getModels();

    // reset all current decorations (KISS)
    for (const model of currentModels) {
      const resourcePath = this._getResourcePath(model['uri']);
      if (this._decorationsMap[resourcePath] && this._decorationsMap[resourcePath].length) {
        const currentDecorationIds = this._decorationsMap[resourcePath].map(d => d['decorationId']);
        model.deltaDecorations(currentDecorationIds, []);
      }
    }

    this._decorationsMap = {};
  }

  /**
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} errors
   * @param {!Array<!yext.m4.errors.DiagnosticData.Diagnostic>} warnings
   * @param {!{deltaDecorations: !Function}}                    model
   * @param {!string}                                           resourcePath
   * @private
   */
  _applyNewErrorsAndWarnings(errors, warnings, model, resourcePath) {
    const newDecorations = [];
    errors.forEach(e =>
      newDecorations.push(
        this._buildDecorationConfig(e.fileLocation.coordinate.row, e.message, 'cac-error')));
    warnings.forEach(w =>
      newDecorations.push(
        this._buildDecorationConfig(w.fileLocation.coordinate.row, w.message, 'cac-warning')));
    const currentDecorations =
      this._decorationsMap[resourcePath]
        ? this._decorationsMap[resourcePath].map(d => d['decorationId']) : [];
    const decorationErrorList = [];
    const decorationIds = model.deltaDecorations(currentDecorations, newDecorations);
    decorationIds.forEach((decorationId, index) => {
      decorationErrorList.push({
        'decorationId': decorationId,
        'diagnostic': errors.concat(warnings)[index],
      });
    });
    this._decorationsMap[resourcePath] = decorationErrorList;
  }

  /**
   * @param   {!number} row
   * @param   {!string} errorMessage
   * @param   {!string} classPrefix
   * @returns {!Object}
   * @private
   */
  _buildDecorationConfig(row, errorMessage, classPrefix) {
    return {
      'range': {
        'startLineNumber': row,
        'startColumn': 1,
        'endLineNumber': row,
        'endColumn': 1,
      },
      'options': {
        'isWholeLine': true,
        'className': classPrefix,
        'glyphMarginClassName': `${classPrefix}-margin`,
        'glyphMarginHoverMessage': {
          'value': errorMessage,
          'isTrusted': true,
        },
        'hoverMessage': {
          'value': errorMessage,
          'isTrusted': true,
        },
      },
    };
  }

  /**
   * @private
   *
   * @param {!{getDecorationRange: !Function}} currentModel
   * @param {!Object} contentChangeEvent
   */
  _checkAndRemoveErrors(currentModel, contentChangeEvent) {
    const resourcePath = this._getResourcePath(currentModel['uri']);
    const decorationsForModel = this._decorationsMap[resourcePath];

    if (!decorationsForModel || !decorationsForModel.length) {
      return;
    }

    const errorsForModel = this._errorsMap[resourcePath] || [];
    const warningsForModel = this._warningsMap[resourcePath] || [];

    if (!errorsForModel.length && !warningsForModel.length) {
      return;
    }

    const changedLineNumbers = [];
    for (const change of contentChangeEvent['changes']) {
      changedLineNumbers.push({
        'start': change['range']['startLineNumber'],
        'end': change['range']['endLineNumber'],
      });
    }
    // For every decoration in the current model:
    // Get the error in monaco, and check if it's in the range.
    const invalidErrorsAndWarnings = new Set();
    for (const decorationObject of decorationsForModel) {
      const decorationId = decorationObject['decorationId'];
      const decorationRange = currentModel.getDecorationRange(decorationId);
      if (!decorationRange) {
        invalidErrorsAndWarnings.add(decorationObject['diagnostic']);
      } else {
        // we assume that errors can only ever be a single line.
        const row = decorationRange['startLineNumber'];
        for (const changedLines of changedLineNumbers) {
          const changedLineNumberStart = changedLines['start'];
          const changedLineNumberEnd = changedLines['end'];
          if (row >= changedLineNumberStart && row <= changedLineNumberEnd) {
            invalidErrorsAndWarnings.add(decorationObject['diagnostic']);
            // no need to keep looking and waste time here, since we already have detected that this error is invalid.
            break;
          }
        }
      }
    }

    if (!invalidErrorsAndWarnings.size) {
      // nothing needs to be changed
      return;
    }

    this._errorsService.removeDiagnostics(
      errorsForModel.filter(e => invalidErrorsAndWarnings.has(e)),
      warningsForModel.filter(e => invalidErrorsAndWarnings.has(e)));
  }

  /**
   * @private
   * @param {!string} text
   * @param {!string} resourcePath
   */
  _setValue(text, resourcePath) {
    // Try and get model if it already exists
    const modelUri = this._getModelUri(resourcePath);
    let model = monaco.editor.getModel(modelUri);

    // If it does not, create a new model
    if (!model) {
      model = monaco.editor.createModel(text, this._inferLanguageMode(resourcePath), modelUri);
    }

    this._codeEditor.setModel(model);
  }

  /**
   * @private
   * Sets the text value of the code editor. Note that this will always create a new monaco model.
   *
   * @param {!string} text
   * @param {!string} resourcePath
   */
  _setValueWithNewModel(text, resourcePath) {
    const modelUri = this._getModelUri(resourcePath);
    let model = monaco.editor.getModel(modelUri);

    // Remove model if it exists
    if (model) {
      model.dispose();
    }

    model = monaco.editor.createModel(text, this._inferLanguageMode(resourcePath), modelUri);
    this._codeEditor.setModel(model);
  }

  /**
   * @param {!Iterable} autoCompletions
   * @return {!Iterable}
   * @private
   */
  _addMonacoAutoCompletionString(autoCompletions) {
    for (let completion of autoCompletions) {
      let propertyName;
      if (completion['isPatternProperty']) {
        propertyName = '<INSERT_PROPERTY_NAME>';
      } else {
        propertyName = completion['propertyName'];
      }
      // this is needed, coz monaco considers $ as some sort of special character for autocomplete
      const stringifiedSnippet = JSON.stringify(completion['snippet'], null, 2)
        .replace(/\$/, '\\$');
      completion['snippetString'] = this._simplifyPlaceHolderStrings(
        `"${propertyName}": ${stringifiedSnippet}`,
        true);
    }
    return autoCompletions;
  }

  /**
   * This function basically replaces the placeholder strings into "tab-replace-able" invalid JSON
   * tokens. If highlight is false, then it does not do the "tab-replace-able" feature.
   *
   * On a high level, the algo does a "find-n-replace with index" with help of js regex
   * functionality in a while true loop, until there are no more placeholder characters.
   *
   * For ex:
   * {
   *  "key1": "<INSERT_NUMBER>",
   *  "key2": "<INSERT_BOOLEAN>"
   * }
   *
   * gets replaced with:
   *
   * {
   *   "key1": ${1:number},
   *   "key2": ${2:boolean}
   * }
   *
   * @param {!string} snippet
   * @param {!boolean} highlight
   * @return {!string}
   * @private
   */
  _simplifyPlaceHolderStrings(snippet, highlight) {
    const placeholderRegexStrings = [
      '<INSERT_BOOLEAN>',
      '<INSERT_NUMBER>',
      '<INSERT_STRING>',
      '<INSERT_PROPERTY_NAME>',
    ];
    let currentIndex = 1;
    let found;
    while (true) {
      found = false;
      let firstPlaceHolder;
      let firstPlaceHolderIndex = null;
      for (let placeHolderString of placeholderRegexStrings) {
        let foundIndex = this._getIndexOfPlaceHolder(snippet, placeHolderString);
        if (foundIndex === -1) {
          continue;
        }
        found = true;
        if (firstPlaceHolderIndex === null || foundIndex < firstPlaceHolderIndex) {
          firstPlaceHolderIndex = foundIndex;
          firstPlaceHolder = placeHolderString;
        }
      }
      if (!found) {
        break;
      }

      let simplifiedPlaceHolder;

      if (highlight) {
        simplifiedPlaceHolder = this._getSnippetString(firstPlaceHolder, currentIndex);
      } else {
        simplifiedPlaceHolder = this._getSnippetString(firstPlaceHolder);
      }

      snippet = snippet.replace(new RegExp('\"' + firstPlaceHolder + '\"'), simplifiedPlaceHolder);

      currentIndex += 1;
    }
    return snippet;
  }

  /**
   * @param {!string} placeHolderString
   * @param {number=} index
   * @return {!string}
   * @private
   */
  _getSnippetString(placeHolderString, index) {
    let snippetString;
    if (placeHolderString.includes('BOOLEAN')) {
      snippetString = 'boolean';
    } else if (placeHolderString.includes('STRING')) {
      snippetString = 'string';
    } else if (placeHolderString.includes('NUMBER')) {
      snippetString = 'number';
    } else if (placeHolderString.includes('PROPERTY_NAME')) {
      snippetString = 'propertyName';
    } else {
      snippetString = placeHolderString;
    }
    if (index) {
      return `\${${index}:${snippetString}}`;
    } else {
      return snippetString;
    }
  }

  /**
   * @private
   * @param {!string} snippet
   * @param {!string} placeholderString
   * @return {!number}
   */
  _getIndexOfPlaceHolder(snippet, placeholderString) {
    const regex = new RegExp(placeholderString);
    return snippet.search(regex);
  }

  /**
   * This basically registers the autocompletion provider. Monaco calls the provideCompletionItems
   * on every alphanumeric keystroke, and maybe on some special characters.
   *
   * @private
   */
  _registerAutoCompletion() {
    monaco.languages.registerCompletionItemProvider('json', {

      'provideCompletionItems': (model, position) => {
        const resourcePath = model['uri']['path'].substr(1);
        const resourceType = this._schemaAssociations[resourcePath];
        if (!resourceType) {
          console.warn('No resource type found for path :', resourcePath);
          return {
            'suggestions': [],
          };
        }

        const autocompletions = this._autoCompletions[resourceType];
        if (!autocompletions) {
          console.warn('No auto completions found for resource type :', resourcePath);
          return {
            'suggestions': [],
          };
        }

        let suggestions = [];
        for (let completion of autocompletions) {
          if (this._checkPositionalContext(model, position, completion)) {
            suggestions.push({
              'label': this._buildAutoCompletionLabel(completion, autocompletions),
              'kind': monaco.languages.CompletionItemKind.Function,
              'documentation': this._buildAutoCompletionDocumentation(completion),
              'insertText': completion['snippetString'],
              'insertTextRules': monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            });
          }
        }
        return {
          'suggestions': suggestions,
        };
      },
    });
  }

  /**
   * Checks if the autocompletion is valid for the current position. Checks if the parent fields
   * are already present.
   *
   * @param {!Object} model
   * @param {!Object} position
   * @param {!Object} completion
   * @return {!boolean}
   * @private
   */
  _checkPositionalContext(model, position, completion) {
    const textUntilPosition = model['getValueInRange']({
      'startLineNumber': 1,
      'startColumn': 1,
      'endLineNumber': position['lineNumber'],
      'endColumn': position['column'],
    });
    let textMatch = true;
    if (!completion['parentIsPatternProperty']
      && completion['parentProperty']
      && !completion['isPatternProperty']) {
      // copy-pasted regex from https://microsoft.github.io/monaco-editor/playground.html#extending-language-services-completion-provider-example
      textMatch = textUntilPosition.match(
        new RegExp(`"${completion['parentProperty']}"` +
          '\\s*:\\s*\\{\\s*("[^"]*"\\s*:\\s*"[^"]*"\\s*,\\s*)*([^"]*)?$'));
    }
    return textMatch;
  }

  /**
   * Builds the label string for autocomplete.
   *
   * @param {!Object} completion
   * @param {!Iterable} autoCompletions
   * @return {!string}
   * @private
   */
  _buildAutoCompletionLabel(completion, autoCompletions) {
    let propName;

    // Handle pattern properties differently.
    if (completion['isPatternProperty']) {
      propName = `Pattern: ${completion['propertyName']}`;
    } else {
      propName = completion['propertyName'];
    }


    let nameCount = 0;
    for (let a of autoCompletions) {
      if (a['propertyName'] === completion['propertyName']) {
        nameCount += 1;
      }
    }

    // If there's more than one snippet with the same field name, list the properties of the snippet
    let fieldsString;
    if (nameCount > 1) {
      if (Array.isArray(completion['snippet'])) {
        if (typeof completion['snippet'][0] === 'object') {
          fieldsString = `[](${Object.keys(completion['snippet'][0]).join(', ')})`;
        }
      } else if (typeof completion['snippet'] === 'object') {
        fieldsString = `(${Object.keys(completion['snippet']).join(', ')})`;
      }
    }

    if (fieldsString) {
      propName += ' ' + fieldsString;
    }

    return propName;
  }

  /**
   * Generates the documentation string in markdown format.
   *
   * @param {!Object} completion
   * @return {!Object}
   * @private
   */
  _buildAutoCompletionDocumentation(completion) {
    let propNameString;
    if (completion['isPatternProperty']) {
      propNameString = `**Property Name:**  \n` +
        `Pattern property with regex \`\`\`${completion['propertyName']}\`\`\``;
    } else {
      propNameString = `**Property Name:**  \n\`\`\`${completion['propertyName']}\`\`\``;
    }

    let descriptionStr;
    if (completion['description']) {
      descriptionStr = `**Description:**  \n${completion['description']}`;
    }

    let parentPropertyString;
    if (completion['parentProperty']) {
      if (completion['isParentPatternProperty']) {
        parentPropertyString = `**Parent Property:**  \n` +
          `Pattern Property with regex \`\`\`${completion['parentProperty']}\`\`\``;
      } else {
        parentPropertyString = `**Parent Property:**  \n` +
          `\`\`\`${completion['parentProperty']}\`\`\``;
      }
    }

    let snippetFields = [];
    let arrayDescription;
    const snippet = completion['snippet'];
    if (Array.isArray(snippet) && snippet[0]) {
      if (typeof snippet[0] === 'object') {
        snippetFields = Object.keys(snippet[0]);
        arrayDescription = `**Array With Fields:**`;
      } else {
        arrayDescription = `Array`;
      }
    } else if (typeof snippet === 'object' && snippet !== null) {
      snippetFields = Object.keys(snippet);
    }

    let typeListStr = '';
    for (let field of snippetFields) {
      if (field === '<INSERT_PROPERTY_NAME>') {
        field = 'Pattern Property Placeholder';
      } else {
        field = `\`\`\`${field}\`\`\``;
      }
      typeListStr += `* ${field}  \n`;
    }

    let typeStr = '';
    if (arrayDescription || typeListStr) {
      if (arrayDescription) {
        typeStr += `${arrayDescription}  \n`;
      } else {
        typeStr += `**Fields:**  \n`;
      }
      if (typeListStr && typeListStr.length) {
        typeStr += typeListStr;
      }
    }

    let docString = propNameString;
    if (descriptionStr) {
      docString += `  \n\n${descriptionStr}`;
    }
    if (parentPropertyString) {
      docString += `  \n\n${parentPropertyString}`;
    }
    if (typeStr && typeStr.length) {
      docString += `  \n\n${typeStr}`;
    }
    return {
      'value': docString,
    };
  }

  /**
   * @param {!string} jsonString - Valid JSON String
   * @return {!Object}
   * @private
   */
  _inferResourceTypeAndVersionFromJsonString(jsonString) {
    let resourceType;
    let version;
    let parsedJSON;
    try {
      parsedJSON = JSON.parse(jsonString);
    } catch (err) {
      return {};
    }

    if (!parsedJSON) {
      return {};
    }

    const schemaField = parsedJSON['$schema'];
    if (schemaField) {
      const rgex = /^(https:\/\/schema.yext.com\/config\/[a-z-]+\/[a-z-]+)\/v/g;
      if (schemaField.match(rgex) == null) {
        return {};
      }
      version = schemaField.replace(rgex, '');
      resourceType = schemaField.split('/config/')[1].replace('/v' + version, '');
    }

    if (version === '' || version === schemaField) {
      return {};
    }

    return {
      'resourceType': resourceType,
      'version': version,
    };
  }

  /**
   * @param {!string} resourceType
   * @param {!string} namespace
   * @param {!string} resourcePath
   * @param {?string} version
   * @param {!Function} callback
   * @returns {*}
   * @private
   */
  _fetchAndUpdateJSONConfigurations(resourceType, namespace, resourcePath, version, callback) {
    const languageMode = this._inferLanguageMode(resourcePath);
    if (languageMode !== 'json') {
      return callback();
    }
    if (this._schemaAssociations[resourcePath]
      && this._schemaMap[resourceType]
      && this._snippetsMap[resourceType]
      && this._autoCompletions[resourceType]) {
      return callback();
    }
    this._schemaAssociations[resourcePath] = resourceType;

    $.ajax({
      method: 'GET',
      url: yext.m4.routes.fetchJSONSchemaConfigurations({
        'resourceType': resourceType,
        'version': version,
        'editorSettingsUriPrefix': this._editorSettingsUriPrefix,
      }),
    }).done(response => {
      let snippetJSON = response['snippet'];
      // Populate the $id by default
      if (resourcePath.endsWith('.json') && snippetJSON) {
        const namespaceDelimiter = response['namespaceDelimiter'] || '/';
        snippetJSON['$id'] = '';
        if (namespace !== 'default') {
          snippetJSON['$id'] = namespace + namespaceDelimiter;
        }

        snippetJSON['$id'] += resourcePath.split('/').slice(-1)[0].split('.')[0];
      }
      const snippetString = snippetJSON ? JSON.stringify(snippetJSON, null, 2) : '';
      this._snippetsMap[resourceType] = this._simplifyPlaceHolderStrings(snippetString, false);
      this._schemaMap[resourceType] = response['jsonSchema'];
      this._autoCompletions[resourceType] =
        this._addMonacoAutoCompletionString(response['autoCompletions']);
      this._updateJSONDiagnostics();
      callback();
    }).fail((_, textStatus, errorThrown) => {
      callback(`${textStatus}: ${errorThrown}`);
    });
  }

  /**
   * @private
   */
  _updateJSONDiagnostics() {
    const monacoSchemaStructure = this._mapToMonacoSchema(this._schemaMap, this._schemaAssociations);
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      // We disable the schema request service because we provide our own schemas
      'enableSchemaRequest': false,
      'validate': true,
      'schemas': monacoSchemaStructure,
    });
  }

  /**
   * @private
   * @param {!string} resourcePath
   * @return {!{
       authority: string,
       fragment: string,
       path: string,
       query: string,
       scheme: string
     }}
   */
  _getModelUri(resourcePath) {
    // Monaco files require a properly formed URI structure.
    // Just picking a random root URI here, it does not matter what it is.
    const rootUri = 'http://resources.yext.com';
    // Encode the % characters in resourcePath since monaco does not handle them properly
    resourcePath = resourcePath.replace(/%/g, '%25');
    return monaco.Uri.parse(`${rootUri}/${resourcePath}`);
  }

  /**
   * @param {!Object} modelUri
   * @return {!string}
   * @private
   */
  _getResourcePath(modelUri) {
    return modelUri['path'].substr(1);
  }


  /**
   * @private
   * Initializes the code editor.
   */
  _initializeCodeEditor(monacoWorkerRoot, theme) {
    // Monaco uses the global MonacoEnvironment.getWorkerUrl to get the path
    // to the worker files, and initialize the respective webworker.
    MonacoEnvironment = {
      getWorkerUrl: function(moduleId, label) {
        if (label === 'json') {
          return monacoWorkerRoot + 'json.worker.bundle.js';
        }
        if (label === 'css') {
          return monacoWorkerRoot + 'css.worker.bundle.js';
        }
        if (label === 'html') {
          return monacoWorkerRoot + 'html.worker.bundle.js';
        }
        if (label === 'typescript' || label === 'javascript') {
          return monacoWorkerRoot + 'ts.worker.bundle.js';
        }
        return monacoWorkerRoot + 'editor.worker.bundle.js';
      },
    };

    this._registerAutoCompletion();

    this._codeEditor = monaco.editor.create(this._domElement, {
      'value': '',
      'language': 'json',
      'fontSize': '16px',
      'minimap': {
        'enabled': false,
      },
      'theme': theme,
      'glyphMargin': true,
    });

    let self = this;
    $(window).resize(function() {
      self._codeEditor.layout();
    });
  }

  /**
   * @private
   *
   * @param {!Object} schemaMap           - The json schemas dict from the backend
   * @param {!Object} schemaAssociations  - The resource path to schema name
   *
   * @return {!Object}
   */
  _mapToMonacoSchema(schemaMap, schemaAssociations) {
    const monacoSchemaMap = {};
    for (const resourcePath of Object.keys(schemaAssociations)) {
      // There's no schema associated with this resource.
      if (!schemaAssociations[resourcePath]) {
        continue;
      }

      const schemaName = schemaAssociations[resourcePath];

      // There's no json-schema associated with the schema name
      if (!schemaMap[schemaName]) {
        continue;
      }

      if (monacoSchemaMap[schemaName]) {
        // We've already created the monaco schema object. Associate this resource to the schema
        monacoSchemaMap[schemaName]['fileMatch'].push(resourcePath);
      } else {
        // Create a new schema for monaco editor and associate this resource with the schema.
        // Note that the URI (identifier) of the schema is set to the properties.$schema.const
        // if present, which is the schema URL with the version number included. If it is not
        // present, then the URI is set to the $id of the schema, which is the the schema URL
        // without the version number.
        monacoSchemaMap[schemaName] = {
          'uri':
            schemaMap[schemaName]['properties']['$schema']
              ? schemaMap[schemaName]['properties']['$schema']['const']
              : schemaMap[schemaName]['$id'],
          'fileMatch': [resourcePath],
          'schema': schemaMap[schemaName],
        };
      }
    }

    return Object.values(monacoSchemaMap);
  }

  /**
   * Returns the language mode
   * @private
   * @param {!string} resourcePath - Path to the resource
   * @return {string|undefined}
   */
  _inferLanguageMode(resourcePath) {
    if (resourcePath.endsWith('.js')) {
      return 'javascript';
    } else if (resourcePath.endsWith('.html')) {
      return 'html';
    } else if (resourcePath.endsWith('.scss') || resourcePath.endsWith('.css')) {
      return 'css';
    } else if (resourcePath.endsWith('.json')) {
      return 'json';
    } else {
      return undefined;
    }
  }
};
